import type { Tag, TagAutocompleteOption } from '@finapp/types';
import type React from 'react';
import { useTagAutocomplete } from '../hooks/useTagAutocomplete';

interface TagSelectorProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  placeholder?: string;
  maxTags?: number;
  'data-testid'?: string;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  placeholder = 'Add tags...',
  maxTags = 10,
  'data-testid': dataTestId,
}) => {
  const {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    showSuggestions,
    setShowSuggestions,
    handleTagSelect,
    handleInputKeyDown,
    handleAddCurrentInput,
  } = useTagAutocomplete({
    onTagResolved: (tag) => {
      if (!selectedTags.some((t) => t.id === tag.id)) {
        onTagsChange([...selectedTags, tag]);
      }
    },
  });

  const handleTagRemove = (tagId: number) => {
    onTagsChange(selectedTags.filter((tag) => tag.id !== tagId));
  };

  // Wrap handleTagSelect to enforce maxTags before delegating to the hook
  const handleTagSelectGuarded = (option: TagAutocompleteOption) => {
    if (selectedTags.length >= maxTags) return;
    handleTagSelect(option);
  };

  return (
    <div
      className="relative"
      data-testid={dataTestId ? `${dataTestId}-container` : 'tag-selector-container'}
    >
      <div
        className="flex flex-wrap gap-1 mb-2"
        data-testid={dataTestId ? `${dataTestId}-tags` : 'tag-selector-tags'}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            data-testid={`tag-${tag.display_name}`}
          >
            {tag.display_name}
            <button
              onClick={() => handleTagRemove(tag.id)}
              className="ml-1 text-blue-600 hover:text-blue-800"
              data-testid={`tag-${tag.display_name}-remove`}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {selectedTags.length < maxTags && (
        <div className="relative">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setShowSuggestions(inputValue.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              data-testid={dataTestId ? `${dataTestId}-input` : 'tag-selector-input'}
            />
            {inputValue.trim() && (
              <button
                type="button"
                onClick={handleAddCurrentInput}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title={`Add "${inputValue.trim()}" as a new tag`}
              >
                Add
              </button>
            )}
          </div>
          {selectedTags.length === 0 && !inputValue && (
            <p className="text-xs text-gray-500 mt-1">
              Type to search existing tags or create new ones. Press Enter or click Add.
            </p>
          )}

          {isLoading && (
            <div className="absolute right-3 top-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}

          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
              {suggestions.map((option, index) => (
                <button
                  key={`${option.value}-${index}`}
                  onClick={() => handleTagSelectGuarded(option)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{option.label}</span>
                    <div className="flex items-center space-x-2">
                      {option.is_new ? (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                          Create new
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                          Use existing
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
